// Process-wide counter of in-flight media uploads (photos and other files —
// comment attachments, resources, voice notes, PDFs — via
// components/shared/uploader.js, videos via videos/upload_video.js).
//
// Consumers that would destroy an in-flight upload check hasActiveUploads()
// before acting — the motivating case is capacitor/resume_refresh.js, which
// hard-reloads the webview after a long background: image uploads have no
// resume, so a reload mid-transfer loses the user's photo outright.
//
// Each upload says what KIND it is ("video", "file"). Most consumers ask about
// all of them; capacitor/upload_keep_awake.js asks about videos only, because
// keeping the phone app awake (an ongoing notification) is worth it for a video
// that compresses and uploads for a minute, not for a photo done in a second.

const counts = new Map();

// kind -> listeners, plus ALL_KINDS for listeners that care about any upload.
const ALL_KINDS = Symbol("all upload kinds");
const listeners = new Map();

const countFor = (kind) =>
  kind === ALL_KINDS
    ? [...counts.values()].reduce((sum, count) => sum + count, 0)
    : (counts.get(kind) ?? 0);

const notify = (kind, active) => {
  for (const listener of listeners.get(kind) ?? []) {
    try {
      listener(active);
    } catch {
      // A listener must never break the upload that changed the count.
    }
  }
};

// Calls the listeners of `kind` and of all kinds whose answer changed.
const change = (kind, delta) => {
  const anyBefore = countFor(ALL_KINDS) > 0;
  const kindBefore = countFor(kind) > 0;
  counts.set(kind, Math.max(0, countFor(kind) + delta));
  const kindAfter = countFor(kind) > 0;
  const anyAfter = countFor(ALL_KINDS) > 0;
  if (kindBefore !== kindAfter) notify(kind, kindAfter);
  if (anyBefore !== anyAfter) notify(ALL_KINDS, anyAfter);
};

// Marks an upload as started. Returns an idempotent `end` function the caller
// MUST invoke when the upload settles (success or failure) — typically from a
// `finally`.
export function beginUploadActivity(kind = "media") {
  change(kind, 1);
  let ended = false;
  return function endUploadActivity() {
    if (ended) return;
    ended = true;
    change(kind, -1);
  };
}

// Any upload at all, or only those of `kind` when one is given.
export function hasActiveUploads(kind) {
  return countFor(kind ?? ALL_KINDS) > 0;
}

/**
 * Calls `listener(active)` whenever hasActiveUploads(kind) changes: true when
 * the first upload of that kind begins, false when the last one ends. Without a
 * kind, it follows all uploads.
 * @param {(active: boolean) => void} listener
 * @param {{kind?: string}} [options]
 * @returns {() => void} unsubscribes.
 */
export function onUploadActivityChange(listener, { kind } = {}) {
  const key = kind ?? ALL_KINDS;
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(listener);
  return () => listeners.get(key)?.delete(listener);
}

// Test-only: reset the counts and listeners between cases without reaching
// into module state.
export function __resetUploadActivityForTests() {
  counts.clear();
  listeners.clear();
}
